import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { Wordmark } from "@/components/shell/wordmark";
import { said, saidByAuth } from "@/lib/errors";

// Where the link in the password reset email lands: with ?token=… when it
// still stands, with ?error=INVALID_TOKEN when it has expired or was used.
export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation();
  const [token] = useState(() =>
    new URLSearchParams(window.location.search).get("token"),
  );
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(token === null);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!token) return;
    setBusy(true);
    try {
      const res = await authClient.resetPassword({ newPassword: password, token });
      if (res.error) {
        if (res.error.code === "INVALID_TOKEN") {
          setExpired(true);
          return;
        }
        throw new Error(saidByAuth(res.error, t("reset.failed")));
      }
      // Every session of the account was signed out, this browser's included.
      window.location.assign("/login?reset=done");
    } catch (error) {
      toast.error(said(error, tc("status.somethingWentWrong")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-svh flex-col px-5 py-8 sm:px-10 sm:py-12">
      <Wordmark />
      <div className="mx-auto my-auto grid w-full max-w-sm gap-6 sm:gap-7">
        <header className="grid gap-2">
          <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
            {t("reset.eyebrow")}
          </span>
          <h1 className="font-display text-[2rem] leading-[1.1] tracking-tight text-ink text-balance sm:text-3xl">
            <Trans
              t={t}
              i18nKey="reset.title"
              components={{ em: <span className="italic" /> }}
            />
          </h1>
        </header>

        {expired ? (
          <div className="grid gap-5">
            <p className="text-sm leading-relaxed text-ink-2">{t("reset.expired")}</p>
            <Button asChild size="lg" variant="outline" className="w-full">
              <Link to="/login">{t("reset.askAgain")}</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-4 sm:gap-5">
            <div className="grid gap-1.5">
              <Label htmlFor="new-password">{t("reset.newPassword")}</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                autoFocus
              />
            </div>
            <Button type="submit" disabled={busy} size="lg" className="mt-1 w-full">
              {busy ? "…" : t("reset.submit")}
            </Button>
          </form>
        )}
      </div>
      <Toaster />
    </main>
  );
}
